import React, { useCallback, useEffect, useState } from 'react';
import { Bug, Play, IndianRupee, CheckCircle2, Loader2, AlertCircle, MapPin, Phone, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';

const STATUS_TONE = {
  open: 'bg-amber-100 text-amber-800',
  assigned: 'bg-indigo-100 text-indigo-700',
  in_progress: 'bg-blue-100 text-blue-700',
  awaiting_payment: 'bg-orange-100 text-orange-800',
  resolved: 'bg-emerald-100 text-emerald-700',
  force_closed: 'bg-slate-200 text-slate-700',
};

export default function EmployeeServiceTicketsTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actOn, setActOn] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await api('GET', '/employee/service-tickets');
      setRows(r.data || []);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="emp-tickets-tab">
      <h2 className="text-lg font-bold flex items-center gap-2"><AlertCircle size={18} className="text-amber-700" /> Assigned Service Tickets</h2>
      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded p-2">{err}</div>}
      {loading && <div className="text-slate-400 text-center py-6">Loading…</div>}
      {!loading && rows.length === 0 && <div className="text-slate-400 text-center py-8 text-sm bg-white rounded-xl border border-slate-200">No tickets assigned to you.</div>}

      {rows.map((t) => {
        const tone = STATUS_TONE[t.status] || STATUS_TONE.open;
        const masked = t._details_masked;
        return (
          <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`emp-tkt-${t.id}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-mono text-xs font-bold">{t.ticket_no}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tone}`}>{(t.status || '').replace(/_/g, ' ').toUpperCase()}</span>
                  {t.is_warranty_claim && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Warranty</span>}
                  {t.priority === 'high' && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">HIGH</span>}
                </div>
                <div className="text-sm font-semibold mt-1"><Bug size={11} className="inline mr-1" /> {t.pest_label || t.pest_type}</div>
                <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{t.description}</div>
                <div className="text-[11px] text-slate-500 mt-1">{t.customer_name} · 🧾 {t.booking_code}</div>
                {t.customer_address && <div className="text-[11px] text-slate-500"><MapPin size={10} className="inline mr-0.5" /> {t.customer_address}</div>}
                {!masked && t.customer_phone && <div className="text-[11px] text-slate-500"><Phone size={10} className="inline mr-0.5" /> {t.customer_phone}</div>}
                {masked && <div className="text-[10px] uppercase font-semibold mt-1 px-2 py-0.5 inline-block rounded bg-slate-100 text-slate-600">🔒 Privacy mode — phone hidden</div>}
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-[10px] uppercase text-slate-500 font-bold">To Collect</div>
                <div className="text-base font-bold text-emerald-700">{fmtMoney(t.charge_amount)}</div>
                <div className="text-[10px] uppercase text-slate-500">{t.charge_status}</div>
              </div>
            </div>

            {t.resolution_notes && (
              <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded p-2 text-xs">
                <div className="font-bold uppercase text-[10px] text-emerald-800">✅ Resolved</div>
                <div className="text-slate-700">{t.resolution_notes}</div>
              </div>
            )}

            {t.status !== 'resolved' && t.status !== 'force_closed' && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                {t.status === 'assigned' && (
                  <Button onClick={() => api('POST', `/employee/service-tickets/${t.id}/start`).then(load)} size="sm" className="bg-blue-700 hover:bg-blue-800" data-testid={`emp-tkt-start-${t.id}`}><Play size={12} className="mr-1" /> Start Work</Button>
                )}
                {t.charge_status !== 'collected' && t.charge_amount > 0 && (
                  <Button onClick={() => setActOn({ ...t, mode: 'collect' })} size="sm" className="bg-emerald-700 hover:bg-emerald-800" data-testid={`emp-tkt-collect-${t.id}`}><IndianRupee size={12} className="mr-1" /> Collect ₹{t.charge_amount}</Button>
                )}
                <Button onClick={() => setActOn({ ...t, mode: 'resolve' })} size="sm" variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" data-testid={`emp-tkt-resolve-${t.id}`}><CheckCircle2 size={12} className="mr-1" /> Mark Resolved</Button>
              </div>
            )}
          </div>
        );
      })}

      {actOn && <ActionModal api={api} ticket={actOn} onClose={() => setActOn(null)} onDone={() => { setActOn(null); load(); }} />}
    </div>
  );
}

function ActionModal({ api, ticket, onClose, onDone }) {
  const isCollect = ticket.mode === 'collect';
  const [amount, setAmount] = useState(ticket.charge_amount || 0);
  const [mode, setMode] = useState('cash');
  const [txnRef, setTxnRef] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      if (isCollect) {
        await api('POST', `/employee/service-tickets/${ticket.id}/collect`, { amount: Number(amount), payment_mode: mode, txn_ref: txnRef || null });
      } else {
        if (!notes.trim()) { setErr('Resolution notes are required'); setBusy(false); return; }
        await api('POST', `/employee/service-tickets/${ticket.id}/resolve`, { resolution_notes: notes.trim() });
      }
      onDone();
    } catch (e2) { setErr(e2.response?.data?.detail || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="emp-tkt-action-modal">
      <form onSubmit={submit} className="bg-white w-full sm:max-w-md rounded-2xl overflow-hidden">
        <div className="px-5 py-3.5 bg-emerald-700 text-white flex items-center justify-between">
          <div><div className="text-xs opacity-90">{ticket.ticket_no}</div><div className="font-bold">{isCollect ? 'Collect Payment' : 'Mark Resolved'}</div></div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {isCollect ? <>
            <div><Label className="text-xs">Amount ₹</Label><Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-10 mt-1" data-testid="emp-tkt-collect-amt" /></div>
            <div>
              <Label className="text-xs">Payment Mode</Label>
              <div className="flex gap-1.5 mt-1">
                {[['cash', 'Cash'], ['upi', 'UPI'], ['card', 'Card']].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setMode(k)} className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold ${mode === k ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'}`} data-testid={`emp-tkt-mode-${k}`}>{l}</button>
                ))}
              </div>
            </div>
            {mode !== 'cash' && <div><Label className="text-xs">Txn Ref (Optional)</Label><Input value={txnRef} onChange={(e) => setTxnRef(e.target.value)} className="h-10 mt-1" data-testid="emp-tkt-txn" /></div>}
          </> : <>
            <div><Label className="text-xs">Resolution Notes *</Label><Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What did you do? e.g., Re-sprayed kitchen + drilling around perimeter, customer satisfied" className="mt-1" data-testid="emp-tkt-notes" required /></div>
          </>}
          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded p-2"><AlertCircle size={14} className="inline mr-1" /> {err}</div>}
        </div>
        <div className="px-5 py-3 bg-slate-50 border-t flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="emp-tkt-action-submit">{busy ? <Loader2 size={13} className="animate-spin mr-1" /> : null} {isCollect ? 'Mark Collected' : 'Mark Resolved'}</Button>
        </div>
      </form>
    </div>
  );
}
